import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, 
  Eye, 
  ShoppingBag, 
  TrendingUp,
  ExternalLink,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

export default function DashboardOverview() {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [stats, setStats] = useState({
    cards: 0,
    orders: 0,
    views: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [cardsRes, ordersRes] = await Promise.all([
        fetch(`${API}/cards`, { headers }),
        fetch(`${API}/orders`, { headers })
      ]);

      if (cardsRes.ok) {
        const cardsData = await cardsRes.json();
        setCards(cardsData);
        setStats(prev => ({ ...prev, cards: cardsData.length }));
      }

      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setRecentOrders(ordersData.slice(0, 3));
        setStats(prev => ({ ...prev, orders: ordersData.length }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      icon: CreditCard, 
      label: 'Active Cards', 
      value: stats.cards,
      color: 'bg-[#0A2540]/50'
    },
    { 
      icon: ShoppingBag, 
      label: 'Total Orders', 
      value: stats.orders,
      color: 'bg-[#00C48C]/50'
    },
    { 
      icon: Eye, 
      label: 'Profile Views', 
      value: stats.views,
      color: 'bg-purple-500'
    },
  ];

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-overview">
      {/* Header */}
      <motion.div {...fadeUp} className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
          {t('dashboard.welcome')}, {user?.first_name || user?.username}!
        </h1>
        <p className="text-slate-600">
          Manage your digital business card and profile
        </p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={idx}
              {...fadeUp}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-soft"
              data-testid={`stat-${idx}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Public Profile */}
        <motion.div {...fadeUp} className="bg-white rounded-2xl p-6 shadow-soft">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
            Your Public Profile
          </h2>
          <div className="flex items-center gap-4 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-2xl font-bold text-slate-400">
                  {user?.first_name?.[0] || user?.username?.[0] || '?'}
                </span>
              </div>
            )}
            <div>
              <p className="font-medium text-slate-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-sm text-slate-500">@{user?.username}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/dashboard/profile">
              <Button variant="outline" className="rounded-xl" data-testid="edit-profile-btn">
                {t('dashboard.editProfile')}
              </Button>
            </Link>
            <a href={`/user/${user?.username}`} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-xl gap-2" data-testid="view-profile-btn">
                <ExternalLink className="w-4 h-4" />
                {t('dashboard.viewProfile')}
              </Button>
            </a>
          </div>
        </motion.div>

        {/* My Cards */}
        <motion.div {...fadeUp} className="bg-white rounded-2xl p-6 shadow-soft">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
            {t('dashboard.card')}
          </h2>
          {cards.length > 0 ? (
            <div className="space-y-3">
              {cards.slice(0, 2).map((card) => (
                <div key={card.card_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{card.serial_number}</p>
                    <p className="text-xs text-slate-500">{card.card_model}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    card.activated ? 'bg-[#00C48C]/10 text-[#00A67E]' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {card.activated ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">{t('dashboard.noCards')}</p>
              <Link to="/products">
                <Button className="rounded-xl gap-2" data-testid="buy-card-btn">
                  <Plus className="w-4 h-4" />
                  {t('dashboard.buyCard')}
                </Button>
              </Link>
            </div>
          )}
        </motion.div>
      </div>

      {/* Recent Orders */}
      <motion.div {...fadeUp} className="bg-white rounded-2xl p-6 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            Recent Orders
          </h2>
          <Link to="/dashboard/orders" className="text-[#0A2540] text-sm hover:text-[#0A2540]">
            View all
          </Link>
        </div>
        {recentOrders.length > 0 ? (
          <div className="space-y-3">
            {recentOrders.map((order) => (
              <div key={order.order_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{order.order_id}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">€{order.total_amount}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    order.status === 'completed' 
                      ? 'bg-[#00C48C]/10 text-[#00A67E]' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-8">No orders yet</p>
        )}
      </motion.div>
    </div>
  );
}
